"use client";

import Link from "next/link";
import {
  isNearMeRealtimeNotification,
  resolveNearMeRealtimeAlertLine,
} from "@/lib/notifications/near-me-alert-copy";
import {
  isNearMeDigestNotification,
  NEAR_ME_DIGEST_INBOX_TITLE,
  parseNearMeDigestInboxMetadata,
  type NearMeDigestInboxMetadata,
} from "@/lib/notifications/near-me-digest-copy";
import { cn } from "@/lib/utils";

export type NearMeInboxItemPresentationProps = {
  eventType?: string | null;
  title: string;
  body: string;
  miniBody?: string | null;
  metadata?: unknown;
  createdAt?: string;
  createdAtLabel?: string;
  variant: "compact" | "full";
  className?: string;
  onLineClick?: () => void;
};

export { isNearMeDigestNotification } from "@/lib/notifications/near-me-digest-copy";

export function isNearMeInboxNotification(eventType?: string | null): boolean {
  return (
    isNearMeRealtimeNotification(eventType) ||
    isNearMeDigestNotification(eventType)
  );
}

function overflowLabel(metadata: NearMeDigestInboxMetadata): string | null {
  if (metadata.overflowCount <= 0) return null;
  return `+${metadata.overflowCount} more on the marketplace`;
}

function resolveDigestPreviewText(
  miniBody: string | null | undefined,
  body: string,
  metadata: NearMeDigestInboxMetadata | null,
): string {
  if (miniBody?.trim()) return miniBody.trim();
  if (metadata?.lines[0]?.alertLine) return metadata.lines[0].alertLine;
  return body.trim() || NEAR_ME_DIGEST_INBOX_TITLE;
}

function TimestampRow({
  createdAt,
  createdAtLabel,
  className,
}: {
  createdAt?: string;
  createdAtLabel?: string;
  className?: string;
}) {
  if (!createdAtLabel) return null;
  return (
    <time
      className={cn(
        "mt-1 block text-right text-[11px] leading-tight text-text-light",
        className,
      )}
      dateTime={createdAt}
    >
      {createdAtLabel}
    </time>
  );
}

export default function NearMeInboxItemPresentation({
  eventType,
  title,
  body,
  miniBody,
  metadata,
  createdAt,
  createdAtLabel,
  variant,
  className,
  onLineClick,
}: NearMeInboxItemPresentationProps) {
  const isCompact = variant === "compact";
  const primaryTextClass = isCompact ? "text-sm" : "text-base";

  if (isNearMeRealtimeNotification(eventType)) {
    const alertLine = resolveNearMeRealtimeAlertLine(title, body);
    return (
      <div className={className}>
        <p className="text-[10px] font-medium uppercase tracking-wide text-text-light">
          New listing near you
        </p>
        <p
          className={cn(
            "mt-1 font-medium leading-snug text-foreground line-clamp-2",
            primaryTextClass,
          )}
        >
          {alertLine}
        </p>
        <TimestampRow createdAt={createdAt} createdAtLabel={createdAtLabel} />
      </div>
    );
  }

  if (isNearMeDigestNotification(eventType)) {
    const parsed = parseNearMeDigestInboxMetadata(metadata);
    const previewText = resolveDigestPreviewText(miniBody, body, parsed);

    if (!isCompact && parsed) {
      const overflow = overflowLabel(parsed);
      return (
        <div className={className}>
          <p className={cn("font-medium text-foreground", primaryTextClass)}>
            {NEAR_ME_DIGEST_INBOX_TITLE}
          </p>
          <ul className="mt-2 space-y-1.5">
            {parsed.lines.map((line) => (
              <li key={line.itemId}>
                <Link
                  href={`/item/${line.itemId}`}
                  className="text-sm text-primary hover:underline"
                  onClick={onLineClick}
                >
                  {line.alertLine}
                </Link>
              </li>
            ))}
          </ul>
          {overflow ? (
            <p className="mt-1 text-xs text-text-light">{overflow}</p>
          ) : null}
          <TimestampRow
            createdAt={createdAt}
            createdAtLabel={createdAtLabel}
            className="mt-2"
          />
        </div>
      );
    }

    return (
      <div className={className}>
        <p
          className={cn(
            "font-medium text-foreground",
            isCompact ? "text-sm" : primaryTextClass,
          )}
        >
          {NEAR_ME_DIGEST_INBOX_TITLE}
        </p>
        <p
          className={cn(
            "mt-1 text-text-light line-clamp-2 whitespace-pre-line",
            isCompact ? "text-xs" : "text-sm",
          )}
        >
          {previewText}
        </p>
        <TimestampRow createdAt={createdAt} createdAtLabel={createdAtLabel} />
      </div>
    );
  }

  return null;
}
